import i18n from '@/i18n/index.js';
import { I18nBase, I18nExtend, LangNps } from '@/i18n/zh-CN-DLC';
import VueI18n from 'vue-i18n';

//_vm才是一手数据(?)
const messages = (i18n as any)._vm.messages as VueI18n.LocaleMessage;

export function getLocaleAlias() {
    return (i18n as any)._vm.locale === 'zh-CN' ? 'chs' : 'eng';
}
/**
 * 针对i18n的封装函数，支持便捷访问语言映射(不用直接调用$t并传入整个路径)
 * 如果你指定了下面所有的参数,寻址顺序将会是main, extend, base,未找到则递进到下一级
 * 从对象的角度看,3个命名空间依次为dlc.base,dlc.base.main,dlc.extend
 * 鉴于有一些文本可以是全局共用的,除了上面的局部寻址外,寻址路径将以dlc.global为兜底
 * 即在最坏的情况下,dlc.global是最后一级寻址空间,这与你在createLanguageMap函数传入什么参数无关
 * @param {String} base 基础命名空间名,这里指dlc下面的子对象
 * @param {String} main 主命名空间,这将是第一个作为搜索对象的命名空间,如基础命名空间下有多级字段,可写在此处(多级需用.分隔)
 * @param {String} extend 除主命名空间外,你可以任意指定一个命名空间作为扩展命名空间,注意要提供全路径
 * @returns 返回一个函数，说明见返回的t函数
 */
export function createLanguageMap<T extends I18nBase>(base?: T, main?: I18nExtend<T>, extend?: string) {
    return t;
    /**
     * 由createLanguageMap返回的t函数,函数会对参数指定的对象进行自动检索,失败会原样返回以方便查错
     * @param {String} str 目标变量的代码
     * @param {String[]} p 格式化参数组,是否传参取决于实际文本是否有这一选项
     * @returns 返回最终的结果字符串
     */
    function t(str: string, ...p: any[]): string {
        try {
            const globalNamespace = messages[i18n.locale].dlc.global;
            const rootNamespace = messages[i18n.locale].dlc;
            const basicNamespace = rootNamespace[base];
            const mainNameSpace = extractObject(basicNamespace, main);
            const extendNamespace = extractObject(rootNamespace, extend);

            if (mainNameSpace[str]) {
                return format(mainNameSpace[str], p);
            }
            else if (extendNamespace[str]) {
                return format(extendNamespace[str], p);
            }
            else if (basicNamespace[str]) {
                return format(basicNamespace[str], p);
            }
            else if (globalNamespace[str]) {
                return format(globalNamespace[str], p);
            }
            else {
                throw new Error();
            }
        }
        catch (e) {
            //方便检查填写情况,返回定位字符串
            return `.${str}`;
        }
    }
}


const path: string[] = [];
export const nsp: LangNps = new Proxy({}, {
    get(t, k, r) {
        path.push(k as string);
        return r;
    },
    set() {
        return false;
    }
}) as any

function getNspRecords() {
    const ret = [...path];
    path.length = 0;

    return ret;
}

/**
 * 针对i18n的封装函数，支持便捷访问语言映射(不用直接调用$t并传入整个路径)
 * 根据传入的命名空间按由内向外的顺序对多语言对象寻址  
 * 指定命名空间的方式为使用nsp对象指定,将访问语句传入statements参数中  
 * 但函数并不会使用此参数,因而参数也不会被检查,需自行保证其正确性  
 * 以createLanguageBubble(nsp.a.b)为例  
 * 此时寻址顺序为dlc.a.b,dlc.a,dlc.global,注意global永远是最后一个可用的命名空间,
 * 尽管还可以提前调用nsp以省略参数,但仍然推荐使用参数的方式来指定空间  
 * @param statements 命名空间定位语句,请使用nsp对象来指定
 * @returns 返回一个函数，说明见返回的t函数
 */
export function createLanguageBubble(statements: string) {
    const bubbles = getNspRecords().reduce((prev, current) => {
        if (prev.length === 0) {
            prev.push(messages[i18n.locale].dlc.global);
            prev.push(messages[i18n.locale].dlc[current]);
        }
        else {
            prev.push(prev[prev.length - 1][current]);
        }

        return prev;
    }, [] as object[]).reverse();

    return t;

    /**
     * 由createLanguageBubble返回的t函数,函数会对参数指定的对象进行自动检索,失败会原样返回以方便查错
     * @param {String} str 目标变量的代码
     * @param {String[]} p 格式化参数组,是否传参取决于实际文本是否有这一选项
     * @returns 返回最终的结果字符串
     */
    function t(str: string, ...p: any[]): string {
        try {
            for (let item of bubbles) {
                if (str in item) {
                    return format(item[str], p);
                }
            }

            throw new Error();
        }
        catch (e) {
            //方便检查填写情况,返回定位字符串
            return `.${str}`;
        }
    }
}

function extractObject(base, path) {
    if (!path) {
        return {};
    }

    const extracted = path.split('.');

    return extracted.reduce((obj, value) => {
        return obj[value];
    }, base);
}

// 因为对调用端是隐藏的,这里说明一下,对于格式化参数,format支持2种形式
// 其一: {n} 形式,其中n为传过来的辅助参数的第n项(以0开始)
// 其二: {name, n}形式,其中name为一个函数指代,即用name对n进行格式化
// name必须在当前语言包下的_f字段内有同名定义,参数参见第一点

function format(str, p) {
    if (!p) {
        return str;
    }
    //没有过多检查,自肃
    return str.replace(/{([^{}]+)}/g, (match, $1) => {
        //若转数字是NaN,则认为是{name,n}的形式
        if (isNaN(parseInt($1))) {
            const messagesFn = (messages[i18n.locale].dlc as any)._f;

            //兼容没有_f的语言包
            if (messagesFn) {
                //允许逗号两边有空格,但n一定要为数字
                const [fn, index] = $1.split(',').map(v => v.trim());

                //检查name是否是messagesFn下的函数,如不是则忽略name,仍解释为{n}
                if (typeof messagesFn[fn] === 'function') {
                    return messagesFn[fn](p[parseInt(index)]);
                }
            }
        }
        return p[$1];
    });
}
